import {Form as AntForm, Modal} from 'antd';
import {SizeType} from 'antd/es/config-provider/SizeContext';
import {FormLayout} from 'antd/es/form/Form';
import {omitBy} from 'lodash-es';
import React, {
    FC, MutableRefObject, useContext, useEffect,
} from 'react';

import {Buttons} from 'components/form/buttons';
import {Fields} from 'components/form/fields/fields';
import {TableModeContext} from 'components/table/context';
import {
    IEntityMeta, selectMetadata, selectTableEntityMetadata, setMetadata,
} from 'entities/metadata';
import {TEntity, TEntityValue} from 'modules/data';
import {
    createActionHandlerForTableChange,
    initializeMetadataAndData,
} from 'modules/data/data-actions';
import {selectFilterEntityData} from 'modules/data/data-selectors';
import {EEntityType} from 'shared/constants/entities';
import {isEmptyObject, isOracleBoolType} from 'shared/utils';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {TModalComponentInterface} from 'store/slices/modals-slice';

import {disabledFields} from '../../button-action-edit-field/button-action-edit-fields';
import {useFilterInitialization} from './hooks';
import {generateActionsForFilter} from './integrated-filter-utils';

interface IOwnProps extends Partial<TModalComponentInterface> {
  entityName: string;
  url?: string;
  parentEntityType?: EEntityType;
  entityType?: EEntityType.FILTER | EEntityType.CONTEXT_FILTER;
  layout?: FormLayout;
  defaultData?: TEntityValue | any;
  defaultMetadata?: IEntityMeta;
  isIntegratedFilter?: boolean;
  spacingValue?: SizeType | number;
  onClick?: () => void;
  checkForDisabledMetaFields?: boolean;
  initializedFilterContext?: MutableRefObject<boolean>;
  isDisablePagination?: boolean;
}

export const IntegratedFilter: FC<IOwnProps> = ({
    entityName,
    entityType = EEntityType.FILTER,
    parentEntityType,
    url,
    defaultMetadata,
    defaultData,
    layout = 'inline',
    isIntegratedFilter = true,
    onClose,
    spacingValue,
    isDisablePagination,
    ...props
}: IOwnProps) => {
    const [form] = AntForm.useForm();
    const {onClick} = props;
    const dispatch = useAppDispatch();
    const tableFieldsMeta = useAppSelector(selectTableEntityMetadata(entityName));
    const filterFieldsMeta = useAppSelector(selectMetadata(entityName, entityType));

    const filterSlice = useAppSelector(selectFilterEntityData(entityName));
    const {resetTableMode} = useContext(TableModeContext);

    const newMetaData = disabledFields(tableFieldsMeta, true);

    useEffect(() => {
        (async () => {
            if (defaultMetadata) {
                await dispatch(initializeMetadataAndData(entityType)(entityName, defaultMetadata, defaultData));
            }
            const dataToSet = filterSlice?.data || defaultData;
            if (dataToSet) form.setFieldsValue(omitBy(dataToSet, (v: any) => isEmptyObject(v)));
        })();
    }, [filterSlice?.data]);

    useFilterInitialization({
        entityName, fields: filterFieldsMeta?.fields, filter: filterSlice,
    });

    const handleValuesChanges = (values: TEntity) => {
        Object.entries(values).forEach(value => {
            if (!value[1]) {
                form.setFieldsValue({[value[0]]: null});
            }
            form.setFieldsValue(value);
        });
    };

    // Обновление данных при изменении фильтров
    const onHandleFinish = (changedValues?: any) => {
        dispatch(createActionHandlerForTableChange(entityType)({
            entityName,
            newEntityTypeData: form.getFieldsValue(),
            url,
            parentEntityType,
            prefix: filterFieldsMeta?.prefix,
            disabledPagination: filterFieldsMeta?.disabledPagination || isDisablePagination,
            skipIfFilterDataIsLoading: true,
        })).then(() => {
            if (filterFieldsMeta?.checkForDisabledMetaFields) {
                dispatch(setMetadata({
                    entityName,
                    entityType: EEntityType.TABLE,
                    metadata: newMetaData,
                }));
            }

            const isBooleanFieldChanged = Object.values(changedValues).reduce((acc, current) => {
                if (isOracleBoolType(current)) {
                    acc = true;
                }

                return acc;
            }, false);

            if (!isBooleanFieldChanged) {
                resetTableMode();
            }
        });

        if (!isIntegratedFilter) {
            onClose?.();
        }
    };

    const defaultFilterActionsMeta = generateActionsForFilter(filterFieldsMeta?.name);

    return isIntegratedFilter ? (
        <AntForm
            {...props}
            colon={false}
            layout={layout}
            form={form}
            onValuesChange={(changedValues) => onHandleFinish(changedValues)}
            onClick={onClick}
        >
            <Fields
                entityName={entityName}
                list={filterFieldsMeta?.fields}
                spacingValue={spacingValue}
            />
        </AntForm>
    ) : (
        <Modal
            title={filterFieldsMeta?.title}
            footer={null}
            visible
            width="100vw"
            onCancel={onClose}
        >
            <AntForm
                layout="vertical"
                form={form}
                onValuesChange={handleValuesChanges}
                onFinish={onHandleFinish}
            >
                <Fields
                    entityName={entityName}
                    list={filterFieldsMeta?.fields}
                />
                <Buttons
                    actions={defaultFilterActionsMeta}
                    onClose={onClose}
                />
            </AntForm>
        </Modal>
    );
};
